import type { SubjectTrack, SubjectTrackView } from "@sitescene/types";

import { SubjectTrackStatus, TrackHopState } from "@sitescene/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiClient } from "@/api/client";
import { ENDPOINTS } from "@/api/endpoints";
import { queryKeys } from "@/api/query-keys";
import { useOrgSelection } from "@/context/use-org-selection";

const TRACK_POLL_MS = 5000;

const OPEN = new Set([
	SubjectTrackStatus.AwaitingSeed,
	SubjectTrackStatus.Expanding,
	SubjectTrackStatus.Indexing,
]);

const CLOSED = new Set([
	SubjectTrackStatus.Dismissed,
	SubjectTrackStatus.Failed,
	SubjectTrackStatus.NoSubject,
]);

/**
 * Still changing: clips indexing, a seed to pick, hops being found, or a
 * decision not yet worked into the hops.
 */
export const isTrackOpen = (
	track: Pick<
		SubjectTrack,
		"expandedRevision" | "revision" | "seedTracklets" | "status"
	>,
): boolean =>
	OPEN.has(track.status) ||
	(!CLOSED.has(track.status) &&
		track.seedTracklets.length > 0 &&
		(track.expandedRevision ?? -1) < track.revision);

/** An operator can no longer change it. */
export const isTrackClosed = (track: Pick<SubjectTrack, "status">): boolean =>
	CLOSED.has(track.status);

const useOrgCode = () => useOrgSelection().selected?.orgCode ?? "";

/** The event's tracks, newest first; polled while any is still changing. */
export const useEventTracks = (eventId: string | undefined) => {
	const orgCode = useOrgCode();
	return useQuery({
		enabled: Boolean(orgCode && eventId),
		queryFn: async () => {
			const response = await apiClient.get<SubjectTrack[]>(
				ENDPOINTS.shadow.events.tracks(eventId ?? ""),
			);
			return response.data;
		},
		queryKey: queryKeys.events.tracks(orgCode, eventId ?? ""),
		refetchInterval: (query) =>
			query.state.data?.some((track) => OPEN.has(track.status))
				? TRACK_POLL_MS
				: false,
	});
};

/** Start tracking a person from the event. Resolves with the new track. */
export const useStartTrack = (eventId: string) => {
	const queryClient = useQueryClient();
	const orgCode = useOrgCode();
	return useMutation({
		mutationFn: async () => {
			const response = await apiClient.post<SubjectTrack>(
				ENDPOINTS.shadow.events.tracks(eventId),
			);
			return response.data;
		},
		onSuccess: () =>
			queryClient.invalidateQueries({
				queryKey: queryKeys.events.tracks(orgCode, eventId),
			}),
	});
};

/**
 * One track with its sightings, polled while it's still changing. Crop and
 * clip URLs are signed for an hour, so a settled track is refetched within it.
 */
export const useTrack = (trackId: string | undefined) => {
	const orgCode = useOrgCode();
	return useQuery({
		enabled: Boolean(orgCode && trackId),
		queryFn: async () => {
			const response = await apiClient.get<SubjectTrackView>(
				ENDPOINTS.shadow.tracks.track(trackId ?? ""),
			);
			return response.data;
		},
		queryKey: queryKeys.tracks.track(orgCode, trackId ?? ""),
		refetchInterval: (query) =>
			query.state.data && isTrackOpen(query.state.data) ? TRACK_POLL_MS : false,
		staleTime: 50 * 60 * 1000,
	});
};

/** Choose the seed, dismiss, or decide on a hop; the track refetches after each. */
export const useTrackActions = (trackId: string) => {
	const queryClient = useQueryClient();
	const orgCode = useOrgCode();
	const refresh = () =>
		queryClient.invalidateQueries({
			queryKey: queryKeys.tracks.track(orgCode, trackId),
		});

	const setSeed = useMutation({
		mutationFn: (seedTracklet: string) =>
			apiClient.patch<SubjectTrack>(ENDPOINTS.shadow.tracks.track(trackId), {
				seedTracklet,
			}),
		onSuccess: refresh,
	});
	const dismiss = useMutation({
		mutationFn: () =>
			apiClient.patch<SubjectTrack>(ENDPOINTS.shadow.tracks.track(trackId), {
				status: SubjectTrackStatus.Dismissed,
			}),
		onSuccess: refresh,
	});
	const review = useMutation({
		mutationFn: ({
			confirmed,
			tracklet,
		}: {
			confirmed: boolean;
			tracklet: string;
		}) =>
			apiClient.patch<SubjectTrack>(
				ENDPOINTS.shadow.tracks.hop(trackId, tracklet),
				{
					state: confirmed ? TrackHopState.Confirmed : TrackHopState.Rejected,
				},
			),
		onSuccess: refresh,
	});
	return { dismiss, review, setSeed };
};
